import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Star, Sparkles, ShieldCheck, GraduationCap, Compass, FileText, Users, MessageCircle,
  Briefcase, Heart, Rocket, HeartPulse, BookOpenCheck, Gem, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { api, waLink } from "../lib/api";
import ZodiacHero from "../components/ZodiacHero";
import Seo from "../lib/Seo";

const whyUs = [
  { icon: ShieldCheck, title: "10+ Years of Experience" },
  { icon: Compass, title: "Personalized Horoscope Analysis" },
  { icon: Sparkles, title: "Practical Vedic Astrology" },
  { icon: GraduationCap, title: "Online Live Courses" },
  { icon: FileText, title: "Detailed PDF Remedies" },
  { icon: Users, title: "Lifetime Learning Support" },
];

const services = [
  { icon: Briefcase, title: "Career Consultation", desc: "Direction, timing and remedial strategy for your professional life." },
  { icon: Heart, title: "Marriage Consultation", desc: "Guna Milan, compatibility and timing of marriage & partnership." },
  { icon: Rocket, title: "Business Astrology", desc: "Launch timing, partnerships and growth cycles for entrepreneurs." },
  { icon: HeartPulse, title: "Health Astrology", desc: "Astrological view of chronic issues, vitality and preventive care." },
  { icon: BookOpenCheck, title: "Child & Education", desc: "Guidance on stream selection, learning patterns and dharma of the child." },
  { icon: Gem, title: "Gemstone & Rudraksha Guidance", desc: "Classical Ratna & Rudraksha prescription for planetary strengthening." },
];

const faqs = [
  { q: "How is Vedic astrology different from Western astrology?", a: "Vedic (Jyotish) uses the sidereal zodiac tied to fixed stars and includes Nakshatras, Dashas and remedies. Western astrology uses the tropical zodiac. The two systems give strikingly different chart placements." },
  { q: "What information do I need to book a consultation?", a: "Your accurate date, time (as precise as possible) and place of birth. If you don't know your exact birth time, we offer birth-time rectification as an add-on." },
  { q: "Do you provide remedies?", a: "Yes — mantras, yantras, gemstones, donations and rituals — all drawn from classical texts. Remedies are prescribed only when they genuinely help." },
  { q: "Are the courses beginner-friendly?", a: "Our Foundation course assumes zero prior knowledge. Advanced programs require completion of the basics or equivalent." },
  { q: "Do you offer online consultations?", a: "Yes — all consultations are conducted online via Zoom or Google Meet with a recording provided." },
];

/* --- Testimonial slider with future-proof pagination ---------------------- */
function TestimonialSlider({ items }) {
  const [i, setI] = useState(0);
  const wrap = useRef(null);
  const count = items.length;
  const step = 1;
  const perView = 1;

  const prev = () => setI((v) => (v - step + count) % count);
  const next = () => setI((v) => (v + step) % count);

  if (count === 0) {
    return (
      <div className="rounded-3xl border border-dashed border-foreground/20 p-14 text-center text-foreground/60">
        <MessageCircle className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
        <div className="font-serif text-2xl">Your review could be featured here.</div>
        <div className="text-sm mt-2">Share your experience with YourAstroHub on WhatsApp.</div>
      </div>
    );
  }

  const t = items[i];
  return (
    <div ref={wrap} className="relative">
      <div className="rounded-3xl border border-foreground/10 bg-background p-8 md:p-12">
        <div className="flex gap-1 text-[#D4AF37] mb-5">
          {[...Array(t.rating)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" />)}
        </div>
        <p className="font-serif text-2xl md:text-3xl leading-snug text-foreground/90">&ldquo;{t.quote}&rdquo;</p>
        <div className="border-t border-foreground/10 pt-5 mt-8">
          <div className="font-semibold text-lg">{t.name}</div>
          <div className="text-xs text-foreground/60 mt-1">{t.role}{t.location ? ` · ${t.location}` : ""}</div>
        </div>
      </div>
      {count > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex gap-1.5">
            {items.map((_, k) => (
              <button
                key={k}
                onClick={() => setI(k)}
                data-testid={`slider-dot-${k}`}
                aria-label={`Go to review ${k+1}`}
                className={`h-1.5 rounded-full transition-all ${k === i ? "w-8 bg-[#D4AF37]" : "w-3 bg-foreground/20 hover:bg-foreground/40"}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={prev} data-testid="slider-prev" aria-label="Previous review" className="w-11 h-11 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center transition-colors"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={next} data-testid="slider-next" aria-label="Next review" className="w-11 h-11 rounded-full border border-foreground/15 hover:border-[#D4AF37] grid place-items-center transition-colors"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [featuredCourse, setFeaturedCourse] = useState(null);
  const [latestBlog, setLatestBlog] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/courses").then((r) => {
      const list = r.data;
      const flagship = list.find((c) => c.popular) || list.find((c) => c.title === "Vedic Jyotish Foundation Course") || list[0];
      setFeaturedCourse(flagship);
    }).catch(() => {});
    api.get("/blog?limit=3").then((r) => setLatestBlog(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Best Vedic Astrologer in India | Online Astrology Consultation & Courses | YourAstroHub"
        description="Book online Vedic astrology consultations and learn authentic Jyotish from Bhupaal Bisht — 10+ years of practical experience in horoscope reading, career astrology, marriage astrology, medical astrology and Vedic courses."
        keywords="Vedic Astrologer, Online Astrology Consultation, Best Vedic Astrologer in India, Horoscope Reading, Astrology Consultation Online, Career Astrology, Marriage Astrology, Medical Astrology, Astrology Courses, Learn Vedic Astrology, Bhupaal Bisht"
        path="/"
        schemas={[{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }]}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] overflow-hidden bg-[#0B1F3A] text-[#F5E8D8]">
        {/* Cosmic backdrop */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A] via-[#0B1F3A] to-[#062036]" />
          <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#0E9F6E]/10 blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#D4AF37]/10 blur-[120px]" />
        </div>

        {/* Stars */}
        {[...Array(50)].map((_, i) => (
          <span key={i} className="star" style={{
            width: `${Math.random() * 2 + 1}px`,
            height: `${Math.random() * 2 + 1}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 4}s`,
          }} />
        ))}

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 pt-28 md:pt-32 pb-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="reveal eyebrow flex items-center gap-3 mb-6">
              <span className="inline-block w-8 gold-hairline" />
              Vedic Jyotish · Mentor Bhupaal Bisht
            </div>
            <h1 data-testid="hero-heading" className="reveal-2 font-serif text-5xl md:text-7xl leading-[1.02] font-light">
              Ancient Wisdom for <span className="gold-text italic">Modern Life.</span>
            </h1>
            <p className="reveal-3 mt-6 text-base md:text-lg text-[#F5E8D8]/75 max-w-xl leading-relaxed">
              Get accurate Vedic astrology consultations, learn authentic Jyotish from experienced mentor
              <span className="text-[#D4AF37]"> Bhupaal Bisht</span>, and discover practical remedies for a happier
              and more successful life.
            </p>
            <div className="reveal-4 mt-9 flex flex-wrap items-center gap-3">
              <Link to="/consultations" data-testid="hero-cta-book">
                <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-7 py-6 group">
                  Book Consultation <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/courses" data-testid="hero-cta-courses">
                <Button size="lg" variant="outline" className="rounded-full border-[#10B981] bg-[#10B981]/10 hover:bg-[#10B981]/20 text-[#10B981] px-7 py-6">
                  Explore Courses
                </Button>
              </Link>
            </div>

            <div className="reveal-4 mt-14 flex flex-wrap gap-8 text-sm">
              {[["10+", "Years"], ["5,000+", "Consultations"], ["1,200+", "Students"], ["4.9★", "Rating"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-serif text-3xl text-[#D4AF37]">{n}</div>
                  <div className="uppercase tracking-[0.2em] text-[10px] text-[#F5E8D8]/60 mt-1">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Illustration */}
          <div className="reveal-3 relative hidden lg:flex justify-center items-center">
            <ZodiacHero />
            {/* Mentor floating card */}
            <Link to="/about" data-testid="hero-mentor-card" className="group absolute bottom-6 left-2 flex items-center gap-3 rounded-full pl-1.5 pr-5 py-1.5 bg-[#0F284B]/85 backdrop-blur-md border border-[#D4AF37]/40 hover:border-[#D4AF37] shadow-2xl transition-all">
              <img src="/assets/bhupaal-bisht.jpg" alt="Bhupaal Bisht" className="w-12 h-12 rounded-full object-cover ring-2 ring-[#D4AF37]" />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Founder & Mentor</div>
                <div className="text-[#F5E8D8] font-serif text-lg leading-tight">Bhupaal Bisht</div>
              </div>
            </Link>
          </div>
        </div>

        {/* mobile illustration + mentor */}
        <div className="relative z-10 lg:hidden pb-8 grid place-items-center px-6">
          <div className="w-[340px] h-[340px] max-w-full">
            <ZodiacHero />
          </div>
          <Link to="/about" className="mt-6 flex items-center gap-3 rounded-full pl-1.5 pr-5 py-1.5 bg-[#0F284B]/85 backdrop-blur-md border border-[#D4AF37]/40">
            <img src="/assets/bhupaal-bisht.jpg" alt="Bhupaal Bisht" className="w-11 h-11 rounded-full object-cover ring-2 ring-[#D4AF37]" />
            <div className="text-left">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Founder & Mentor</div>
              <div className="text-[#F5E8D8] font-serif text-base leading-tight">Bhupaal Bisht</div>
            </div>
          </Link>
        </div>
      </section>

      {/* WHY US */}
      <section data-testid="home-why-us" className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="eyebrow mb-3">Why Choose YourAstroHub</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Rooted in tradition. <span className="emerald-text italic">Ready for today.</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
            {whyUs.map((w) => (
              <div key={w.title} data-testid={`why-${w.title.replace(/\s+/g,'-').toLowerCase()}`} className="group p-6 md:p-7 rounded-3xl bg-background border border-foreground/10 hover:border-[#10B981]/60 transition-all hover:-translate-y-1 hover:shadow-xl">
                <div className="w-11 h-11 rounded-2xl grid place-items-center bg-[#10B981]/10 border border-[#10B981]/30 mb-5 group-hover:bg-[#D4AF37]/15 group-hover:border-[#D4AF37]/50 transition-colors">
                  <w.icon className="w-5 h-5 text-[#10B981] group-hover:text-[#D4AF37] transition-colors" />
                </div>
                <h3 className="font-serif text-xl md:text-2xl leading-tight">{w.title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FREE TOOLS */}
      <section data-testid="home-free-tools" className="py-16 md:py-20 bg-background border-y border-foreground/10">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-10">
            <div className="eyebrow mb-3 inline-flex items-center gap-2 justify-center"><span className="w-8 gold-hairline" />Free Astrology Tools<span className="w-8 gold-hairline" /></div>
            <h2 className="font-serif text-3xl md:text-5xl font-light">Try our <span className="gold-text italic">free calculators</span> before booking.</h2>
            <p className="text-foreground/60 mt-3 max-w-xl mx-auto">Swiss Ephemeris-powered accuracy · Sidereal Lahiri Ayanamsa · No sign-up required.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
            <Link to="/kundali-generator" data-testid="home-tool-kundali"
              className="group rounded-3xl border border-foreground/10 hover:border-[#D4AF37] bg-[#0B1F3A] text-[#F5E8D8] p-7 md:p-8 transition-all hover:-translate-y-1 hover:shadow-xl overflow-hidden relative">
              <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#D4AF37]/10 blur-2xl group-hover:bg-[#D4AF37]/20 transition-colors" />
              <div className="relative">
                <div className="w-11 h-11 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 mb-4">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">Free Kundali</div>
                <h3 className="font-serif text-2xl md:text-3xl mt-2">Generate Your Vedic Birth Chart</h3>
                <p className="text-[#F5E8D8]/70 text-sm mt-3">Lagna, D1 &amp; D9 charts, planetary positions, Nakshatra, Pada and Vimshottari Dasha — instantly.</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] group-hover:gap-3 transition-all">
                  Generate my Kundali <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
            <Link to="/janam-nakshatra" data-testid="home-tool-nakshatra"
              className="group rounded-3xl border border-[#D4AF37]/40 hover:border-[#D4AF37] bg-background p-7 md:p-8 transition-all hover:-translate-y-1 hover:shadow-xl overflow-hidden relative">
              <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-[#D4AF37]/10 blur-2xl group-hover:bg-[#D4AF37]/15 transition-colors" />
              <div className="relative">
                <div className="w-11 h-11 rounded-full grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/40 mb-4">
                  <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">Janam Nakshatra</div>
                <h3 className="font-serif text-2xl md:text-3xl mt-2">Find Your Birth Nakshatra &amp; Pada</h3>
                <p className="text-foreground/70 text-sm mt-3">Discover your Nakshatra, Rashi, Pada and Nakshatra Lord — with a short educational description.</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] group-hover:gap-3 transition-all">
                  Find my Nakshatra <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="home-services" className="py-24 md:py-28 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-12 gap-8 mb-12">
            <div className="md:col-span-6">
              <div className="eyebrow mb-3">Services</div>
              <h2 className="font-serif text-4xl md:text-5xl font-light">Guidance for every <span className="gold-text italic">life season.</span></h2>
            </div>
            <p className="md:col-span-6 text-foreground/70 self-end text-lg leading-relaxed">
              Every service is grounded in classical Parashari Jyotish and refined with practical, remedial insight.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <div key={s.title} data-testid={`service-card-${i}`} className="group p-8 rounded-3xl bg-background border border-foreground/10 hover:border-[#D4AF37]/60 transition-all hover:-translate-y-1 hover:shadow-2xl flex flex-col">
                <div className="w-12 h-12 rounded-2xl grid place-items-center bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
                  <s.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-2xl mb-2">{s.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed mb-6 flex-1">{s.desc}</p>
                <Link to="/consultations" data-testid={`service-know-${i}`} className="text-sm font-medium text-[#0E9F6E] hover:text-[#D4AF37] inline-flex items-center gap-1 self-start">
                  Know More <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED COURSE */}
      <section data-testid="home-courses" className="py-24 md:py-28 bg-[#0B1F3A] text-[#F5E8D8] relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#10B981]/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="eyebrow mb-3">Featured Course</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Learn Jyotish the <span className="gold-text italic">right way.</span></h2>
          </div>

          {featuredCourse && (
            <div data-testid="featured-course-card" className="grid md:grid-cols-12 gap-6 items-center rounded-3xl border border-[#D4AF37]/30 bg-[#0F284B] overflow-hidden">
              <div className="md:col-span-5">
                <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden">
                  <img src={featuredCourse.cover_image} alt={featuredCourse.title} className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="md:col-span-7 p-8 md:p-12">
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.24em] font-bold ${featuredCourse.type === "live" ? "bg-[#D4AF37] text-[#0B1F3A]" : "bg-[#10B981] text-white"}`}>{featuredCourse.type === "live" ? "Live Cohort" : "Recorded"}</span>
                  {featuredCourse.badge && (
                    <span className="px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/40 text-[10px] uppercase tracking-[0.24em] font-semibold">{featuredCourse.badge}</span>
                  )}
                </div>
                <h3 className="font-serif text-3xl md:text-4xl leading-tight mb-3">{featuredCourse.title}</h3>
                <p className="text-[#F5E8D8]/75 mb-6 leading-relaxed">{featuredCourse.description}</p>

                <div className="grid grid-cols-3 gap-3 mb-8">
                  <div className="rounded-2xl border border-[#D4AF37]/25 p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5E8D8]/60">Duration</div>
                    <div className="font-serif text-lg text-[#F5E8D8] mt-1">{featuredCourse.duration}</div>
                  </div>
                  <div className="rounded-2xl border border-[#D4AF37]/25 p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5E8D8]/60">Level</div>
                    <div className="font-serif text-lg text-[#F5E8D8] mt-1">{featuredCourse.level}</div>
                  </div>
                  <div className="rounded-2xl border border-[#D4AF37]/25 p-4">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#F5E8D8]/60">Fees</div>
                    <div className="font-serif text-2xl text-[#D4AF37] mt-1">₹{featuredCourse.fees.toLocaleString()}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <a href={waLink(`Hello, I would like to enroll in the ${featuredCourse.title} (₹${featuredCourse.fees.toLocaleString()}).`)} target="_blank" rel="noreferrer" data-testid="course-enroll-cta">
                    <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                      Enroll Now <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                  <Link to="/courses">
                    <Button size="lg" variant="outline" className="rounded-full border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10 px-8 py-6">
                      All Courses
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS SLIDER */}
      <section data-testid="home-testimonials" className="py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">Student & Client Voices</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Stories from the <span className="gold-text italic">seekers we serve.</span></h2>
          </div>
          <TestimonialSlider items={testimonials} />
        </div>
      </section>

      {/* BLOG */}
      <section data-testid="home-blog" className="py-24 bg-[#F4EFE6] dark:bg-[#0F284B]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="flex justify-between items-end mb-12 flex-wrap gap-4">
            <div>
              <div className="eyebrow mb-3">From the Journal</div>
              <h2 className="font-serif text-4xl md:text-5xl font-light">Latest astrology articles.</h2>
            </div>
            <Link to="/blog" className="text-[#0E9F6E] hover:text-[#D4AF37] inline-flex items-center gap-2 transition-colors">All articles <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {latestBlog.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group rounded-3xl overflow-hidden bg-background border border-foreground/10 hover:border-[#D4AF37]/50 transition-all hover:-translate-y-1">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37] mb-3">{p.category}</div>
                  <h3 className="font-serif text-xl leading-tight mb-2 group-hover:text-[#D4AF37] transition-colors">{p.title}</h3>
                  <p className="text-sm text-foreground/70 line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="home-faq" className="py-24 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <div className="text-center mb-12">
            <div className="eyebrow mb-3">Frequently Asked</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light">Your questions, answered.</h2>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-foreground/10">
                <AccordionTrigger data-testid={`faq-q-${i}`} className="text-left font-serif text-xl md:text-2xl py-6 hover:text-[#D4AF37] hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-foreground/75 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="pb-24">
        <div className="max-w-6xl mx-auto px-6 md:px-8">
          <div className="relative rounded-3xl overflow-hidden p-10 md:p-16 bg-[#0B1F3A] text-[#F5E8D8] grain text-center">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#10B981]/25 blur-3xl rounded-full" />
            <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-[#D4AF37]/20 blur-3xl rounded-full" />
            <div className="relative">
              <div className="eyebrow mb-4">A Better Tomorrow Starts Today</div>
              <h3 className="font-serif text-4xl md:text-6xl font-light leading-tight">
                Ready to Transform <span className="gold-text italic">Your Life?</span>
              </h3>
              <p className="mt-5 text-[#F5E8D8]/75 max-w-2xl mx-auto text-lg">
                Book your consultation or join our astrology courses today.
              </p>
              <div className="mt-9 flex flex-wrap gap-3 justify-center">
                <a href={waLink("Hello, I would like to book an astrology consultation with YourAstroHub.")} target="_blank" rel="noreferrer" data-testid="cta-whatsapp-btn">
                  <Button size="lg" className="rounded-full bg-[#25D366] hover:bg-[#20b957] text-white font-semibold px-8 py-6">
                    <MessageCircle className="w-4 h-4 mr-2" />WhatsApp Now
                  </Button>
                </a>
                <Link to="/consultations">
                  <Button size="lg" className="rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A] font-semibold px-8 py-6">
                    Book Consultation <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
